import React, { useContext, useEffect, useState } from 'react';
import { BubbleOverlay } from '@go-client/components/BubbleUI';
import { Property } from '@go-shared/types/property';
import { getPropertiesOfBrand, getProperty } from '@go-client/lib/api/property';
import { populate } from '@go-shared/lib/property';
import { useServerInfo } from './ServerInfoContext';
import { useUserInfo } from './UserInfoContext';
import { getUserProperties } from '@go-shared/lib/user';
import { safeIdCompare } from '@go-shared/lib/utils';
import { mapBy, MappedItems } from '@go-shared/lib/functional';

interface RefreshPropertyInfoContextOptions {
  specificPropertyId?: string;
}

export interface PropertyInfoBundle {
  myProperties: Property[]; // use this one for only the properties you have access to
  allProperties: Property[]; // use this one to get all properties of that brand
  allPropertiesDict: MappedItems<Property>;
  refreshContext: (options?: RefreshPropertyInfoContextOptions) => Promise<void>;
}

const PropertyInfoContext = React.createContext<PropertyInfoBundle | undefined>(undefined);

export function usePropertyInfo() {
  const context = useContext(PropertyInfoContext);

  if (context === undefined) {
    throw new Error('usePropertyInfo must be used within a PropertyInfoProvider');
  }

  return context;
}

export function WithPropertyInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  const [propertyInfoBundle, setPropertyInfoBundle] = useState<PropertyInfoBundle | undefined>(
    undefined
  );

  const { brand, instanceRole, property } = useServerInfo();
  const { user } = useUserInfo();

  async function fetchPropertyInfo(
    prevPropertyInfoBundle?: Omit<PropertyInfoBundle, 'refreshContext'>,
    options?: RefreshPropertyInfoContextOptions
  ) {
    let newProperties: Property[] = [];

    if (options && options.specificPropertyId && prevPropertyInfoBundle) {
      const propsCopy = [...prevPropertyInfoBundle.allProperties];

      const newBareProperty = await getProperty(options.specificPropertyId);
      const newProperty = populate(newBareProperty, brand);

      propsCopy.splice(
        propsCopy.findIndex(p => p._id === newProperty._id),
        1,
        newProperty
      );
      newProperties = propsCopy;
    } else {
      const newBareProperties = await getPropertiesOfBrand(brand._id);
      newProperties = newBareProperties.map(bp => populate(bp, brand));
    }

    let userProperties = getUserProperties(user, newProperties);

    if (instanceRole === 'property' && property) {
      userProperties = userProperties.filter(p => safeIdCompare(p._id, property._id));
    }

    const partialState = {
      allProperties: newProperties,
      myProperties: userProperties,
      allPropertiesDict: mapBy(newProperties, '_id'),
    };

    setPropertyInfoBundle({
      ...partialState,
      refreshContext: (opts?: RefreshPropertyInfoContextOptions) =>
        fetchPropertyInfo(partialState, opts),
    });
  }

  useEffect(() => {
    fetchPropertyInfo().catch(console.error);
  }, [brand]);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  return (
    <BubbleOverlay loading={!propertyInfoBundle} {...rest} className={classes.join(' ')}>
      {propertyInfoBundle && (
        <>
          <PropertyInfoProvider propertyInfoBundle={{ ...propertyInfoBundle }}>
            {props.children}
          </PropertyInfoProvider>
        </>
      )}
    </BubbleOverlay>
  );
}

interface PropertyInfoProviderPropsBase {
  propertyInfoBundle: PropertyInfoBundle;
}

type PropertyInfoProviderProps = PropertyInfoProviderPropsBase & { children?: React.ReactNode };

export default function PropertyInfoProvider(props: PropertyInfoProviderProps) {
  const { propertyInfoBundle, children } = props;

  return (
    <PropertyInfoContext.Provider value={propertyInfoBundle}>
      {children}
    </PropertyInfoContext.Provider>
  );
}
